// PRD 14 §3.5 — inline add/edit form for term types.
// Self-contained: owns local field state + the create/update mutation. When
// used as an add form (`type === null`) it remains mounted at the bottom of
// the panel; when used as an edit form it replaces the row inline.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/components/feedback/useToast';
import { hasEmoji } from '@/shared/utils/emoji';

import {
  useCreateTermType,
  useUpdateTermType,
} from '../hooks/use-term-mutations';
import { TermsApiError, type TermType } from '../types/terms.types';
import { getTermsErrorMessage } from '../utils/error-message';

interface TypeEditFormProps {
  // null = add-mode (empty defaults); a TermType = edit-mode (pre-filled).
  type: TermType | null;
  onClose: () => void;
}

const MAX_NAME = 60;
const MAX_DESCRIPTION = 200;

export function TypeEditForm({ type, onClose }: TypeEditFormProps) {
  const { t } = useTranslation('terms');

  const isEdit = type !== null;
  const [name, setName] = useState(type?.name ?? '');
  const [required, setRequired] = useState<boolean>(type?.required ?? true);
  const [description, setDescription] = useState(type?.description ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const createMutation = useCreateTermType();
  const updateMutation = useUpdateTermType();
  const submitting = createMutation.isPending || updateMutation.isPending;

  const clearError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Mirror the mock + contract §1.7 validations so the user gets feedback
  // before the network round-trip for the simple cases.
  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    const trimmedName = name.trim();
    if (!trimmedName) errors.name = t('errors.TYPE_NAME_REQUIRED');
    else if (trimmedName.length > MAX_NAME) errors.name = t('errors.TYPE_NAME_TOO_LONG');
    // SHCT-859 — NAME / DESC classes: 이모지 불허 ([공통] 00 §4.2).
    else if (hasEmoji(trimmedName)) errors.name = t('common:errors.emojiNotAllowed');
    if (description.length > MAX_DESCRIPTION) {
      errors.description = t('errors.TYPE_DESCRIPTION_TOO_LONG');
    } else if (hasEmoji(description)) {
      errors.description = t('common:errors.emojiNotAllowed');
    }
    return errors;
  };

  const handleSave = async () => {
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }
    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          id: type.id,
          input: {
            name: name.trim(),
            required,
            description,
          },
        });
        toast.success(t('toasts.typeUpdated'));
      } else {
        await createMutation.mutateAsync({
          name: name.trim(),
          required,
          description,
        });
        toast.success(t('toasts.typeAdded'));
      }
      onClose();
    } catch (err) {
      if (err instanceof TermsApiError && err.fields && Object.keys(err.fields).length > 0) {
        setFieldErrors(err.fields);
      } else {
        toast.error(
          getTermsErrorMessage(
            err,
            t,
            isEdit ? 'errors.updateTypeFailed' : 'errors.addTypeFailed',
          ),
        );
      }
    }
  };

  return (
    <div
      style={{
        padding: 'var(--space-3)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--card)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground)' }}>
        {isEdit ? t('typeManager.form.titleEdit') : t('typeManager.form.titleNew')}
      </div>

      <div className="form-field" style={{ marginBottom: 0 }}>
        <label className="form-field__label">
          {t('typeManager.form.fields.name.label')} <span className="required">*</span>
        </label>
        <input
          type="text"
          className={`form-field__input${fieldErrors.name ? ' form-field__input--error' : ''}`}
          maxLength={MAX_NAME}
          placeholder={t('typeManager.form.fields.name.placeholder')}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearError('name');
          }}
        />
        {fieldErrors.name ? <p className="form-field__error">{fieldErrors.name}</p> : null}
      </div>

      <div className="form-field" style={{ marginBottom: 0 }}>
        <label className="form-field__label">
          {t('typeManager.form.fields.required.label')} <span className="required">*</span>
        </label>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="termTypeRequired"
              checked={required}
              onChange={() => setRequired(true)}
            />
            {t('typeManager.form.fields.required.required')}
          </label>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="termTypeRequired"
              checked={!required}
              onChange={() => setRequired(false)}
            />
            {t('typeManager.form.fields.required.optional')}
          </label>
        </div>
      </div>

      <div className="form-field" style={{ marginBottom: 0 }}>
        <label className="form-field__label">
          {t('typeManager.form.fields.description.label')}
        </label>
        <input
          type="text"
          className={`form-field__input${
            fieldErrors.description ? ' form-field__input--error' : ''
          }`}
          maxLength={MAX_DESCRIPTION}
          placeholder={t('typeManager.form.fields.description.placeholder')}
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            clearError('description');
          }}
        />
        {fieldErrors.description ? (
          <p className="form-field__error">{fieldErrors.description}</p>
        ) : null}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-1)',
        }}
      >
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={onClose}
          disabled={submitting}
        >
          {t('typeManager.form.actions.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleSave}
          disabled={submitting}
        >
          {submitting
            ? t('typeManager.form.actions.saving')
            : t('typeManager.form.actions.save')}
        </button>
      </div>
    </div>
  );
}
